import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Inbox, Calendar, Clock, Activity, RefreshCw, Search,
    Upload, Mail, MoreHorizontal, Trash2, RotateCw, ExternalLink,
    CheckCircle2, Loader2, AlertTriangle,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import CompanyPecBanner from "../components/CompanyPecBanner";
import LoadingState from "../components/LoadingState";
import EmptyState from "../components/EmptyState";
import RiskBadge from "../components/RiskBadge";
import TechnicalStatusBadge from "../components/TechnicalStatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../lib/auth";
import api from "../lib/api";

const REFRESH_MS = 30_000;
const IDLE_MS = 5 * 60_000;

/**
 * Show up to 2 recipients inline; if more, append a "+N altri" chip with
 * a full-list tooltip. Fully accessible via native `title` attribute.
 */
function RecipientsCell({ recipients, count }) {
    const total = count ?? (recipients || []).length;
    if (!total) return <span className="text-[#94A3B8]">—</span>;
    const shown = (recipients || []).slice(0, 2);
    const extra = Math.max(0, total - shown.length);
    const tooltip = (recipients || []).join(", ");
    return (
        <div className="max-w-[170px]" title={tooltip}>
            <div className="text-[#172033] truncate">{shown.join(", ")}</div>
            {extra > 0 && (
                <span
                    data-testid="pec-inbox-recipients-more"
                    className="inline-block mt-0.5 text-[10px] font-medium text-[#64748B] bg-[#F1F5F9] border border-[#DCE3EE] rounded px-1.5 py-0.5"
                >
                    +{extra} altri
                </span>
            )}
        </div>
    );
}

function fmtDT(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", {
            timeZone: "Europe/Rome",
            day: "2-digit", month: "2-digit",
            hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
    } catch { return iso; }
}
function fmtDTFull(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" }); }
    catch { return iso; }
}

/**
 * Compact 2-line date cell: DD/MM on top, HH:MM below.
 * Consumes ~64px instead of ~100px of a single-line "DD/MM, HH:MM:SS".
 */
function DateCell({ iso }) {
    if (!iso) return <span className="text-[#94A3B8]">—</span>;
    let dd = "—", tt = "";
    try {
        const d = new Date(iso);
        dd = d.toLocaleDateString("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit" });
        tt = d.toLocaleTimeString("it-IT", { timeZone: "Europe/Rome", hour: "2-digit", minute: "2-digit" });
    } catch { /* noop */ }
    return (
        <div className="leading-tight" title={fmtDTFull(iso)}>
            <div className="text-[#172033] font-medium text-xs">{dd}</div>
            <div className="text-[10px] text-[#94A3B8]">{tt}</div>
        </div>
    );
}

function truncate(s, n = 60) {
    if (!s) return "";
    return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function PipelineBadge({ status, step, name }) {
    if (status === "completed") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] bg-[#16A34A]/10 text-[#166534] border border-[#16A34A]/30">
                <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} /> Completata
            </span>
        );
    }
    if (status === "running") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] bg-[#2563EB]/10 text-[#1D4ED8] border border-[#2563EB]/30">
                <Loader2 className="w-3 h-3 animate-spin" />
                {step ? `Step ${step}/10 · ${name || "…"}` : "In corso"}
            </span>
        );
    }
    if (status === "error") {
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] bg-[#DC2626]/10 text-[#991B1B] border border-[#DC2626]/30">
                <AlertTriangle className="w-3 h-3" strokeWidth={2.5} />
                Errore{step ? ` step ${step}` : ""}
            </span>
        );
    }
    return <span className="text-xs text-[#64748B]">—</span>;
}

function riskLevel(score) {
    if (typeof score !== "number") return null;
    if (score >= 80) return "Critico";
    if (score >= 60) return "Alto";
    if (score >= 40) return "Medio";
    if (score >= 20) return "Attenzione";
    return "Basso";
}

function CounterCard({ testid, icon: Icon, label, value, pulse }) {
    return (
        <div data-testid={testid}
            className="flex items-center gap-3 rounded-xl border border-[#DCE3EE] bg-white px-4 py-3 min-w-[160px]">
            <div className={`w-9 h-9 rounded-lg bg-[#2563EB]/10 flex items-center justify-center ${pulse && value > 0 ? "animate-pulse" : ""}`}>
                <Icon className="w-4.5 h-4.5 text-[#2563EB]" strokeWidth={2.25} />
            </div>
            <div>
                <div className="text-2xl font-bold text-[#172033] leading-none">{value}</div>
                <div className="text-[11px] text-[#64748B] mt-1">{label}</div>
            </div>
        </div>
    );
}

export default function PecInbox() {
    const { user, companyId, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState({ today: 0, last_14_days: 0, in_pipeline: 0 });
    const [loading, setLoading] = useState(true);
    const [mailboxes, setMailboxes] = useState([]);
    const [filters, setFilters] = useState({
        source: "all", pipeline_status: "all",
        from: "", to: "", include_older: false,
    });
    const [busy, setBusy] = useState(null);
    const [reanalyzeFor, setReanalyzeFor] = useState(null);
    const [deleteFor, setDeleteFor] = useState(null);
    const [deleteReason, setDeleteReason] = useState("");
    const [autoPaused, setAutoPaused] = useState(false);
    const failCountRef = useRef(0);
    const lastActivityRef = useRef(Date.now());

    // Role checks — analista+ can re-analyze; only amministratore/super_admin can delete
    const role = user?.companies?.find?.((c) => c.id === companyId)?.role
        || user?.role_global;
    const canReanalyze = isSuperAdmin() || role === "amministratore" || role === "analista";
    const canDelete = isSuperAdmin() || role === "amministratore";

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            // Short-circuit: super_admin in "Tutte le aziende" mode → skip the call
            // (the guard below renders the select-company banner). Avoids a pointless 400.
            const isSuperAll =
                (user?.role_global === "super_admin") &&
                (!companyId || companyId === "__all__");
            if (isSuperAll) {
                setItems([]);
                setCounters({ today: 0, last_14_days: 0, in_pipeline: 0 });
                return;
            }
            const params = {};
            if (filters.source !== "all") params.source = filters.source;
            if (filters.pipeline_status !== "all") params.pipeline_status = filters.pipeline_status;
            if (filters.from) params.from = new Date(filters.from + "T00:00:00").toISOString();
            if (filters.to) params.to = new Date(filters.to + "T23:59:59").toISOString();
            if (filters.include_older) params.include_older = "true";
            const { data } = await api.get("/pec/inbox", { params });
            setItems(data.items || []);
            setCounters(data.counters || { today: 0, last_14_days: 0, in_pipeline: 0 });
            failCountRef.current = 0;
            setAutoPaused(false);
        } catch (e) {
            failCountRef.current += 1;
            if (failCountRef.current >= 3) setAutoPaused(true);
            // Silently swallow the well-known 'X-Company-Id missing' 400 — the page
            // renders a dedicated banner in that case (see below).
            const detail = e?.response?.data?.detail || "";
            if (!silent && !detail.includes("X-Company-Id")) {
                toast.error(detail || "Errore di caricamento");
            }
            setItems([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [filters, companyId, user]);

    // Load mailboxes for the source filter
    useEffect(() => {
        api.get("/mailboxes").then((r) => setMailboxes(r.data?.items || [])).catch(() => { });
    }, []);

    useEffect(() => { load(); /* on filters change */ }, [load]);

    // Auto-refresh with idle + failure pause
    useEffect(() => {
        const activityHandler = () => { lastActivityRef.current = Date.now(); };
        ["mousemove", "keydown", "click", "scroll"].forEach((ev) =>
            window.addEventListener(ev, activityHandler, { passive: true }));
        const timer = setInterval(() => {
            const idle = Date.now() - lastActivityRef.current > IDLE_MS;
            const hidden = document.visibilityState !== "visible";
            if (idle || hidden || autoPaused) return;
            load(true);
        }, REFRESH_MS);
        return () => {
            clearInterval(timer);
            ["mousemove", "keydown", "click", "scroll"].forEach((ev) =>
                window.removeEventListener(ev, activityHandler));
        };
    }, [load, autoPaused]);

    const doReanalyze = async () => {
        if (!reanalyzeFor) return;
        setBusy(reanalyzeFor.id);
        try {
            await api.post(`/pec/${reanalyzeFor.id}/re-analyze`);
            toast.success("Ri-analisi avviata, aggiornamento in corso…");
            setReanalyzeFor(null);
            setTimeout(() => load(true), 2000);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setBusy(null); }
    };

    const doDelete = async () => {
        if (!deleteFor) return;
        if (!deleteReason.trim() || deleteReason.trim().length < 10) {
            toast.error("Motivo obbligatorio (min 10 caratteri)");
            return;
        }
        setBusy(deleteFor.id);
        try {
            await api.delete(`/pec/${deleteFor.id}`, { data: { reason: deleteReason.trim() } });
            toast.success("PEC eliminata");
            setDeleteFor(null);
            setDeleteReason("");
            setItems((prev) => prev.filter((p) => p.id !== deleteFor.id));
            load(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setBusy(null); }
    };

    const filtersActive =
        filters.source !== "all" ||
        filters.pipeline_status !== "all" ||
        filters.from || filters.to || filters.include_older;

    const clearFilters = () => setFilters({ source: "all", pipeline_status: "all", from: "", to: "", include_older: false });

    if (loading) return <LoadingState label="Caricamento inbox…" />;

    // Super_admin without a company selected → guide the user to pick one.
    const isSuper = isSuperAdmin();
    const isAllCompaniesMode = !companyId || companyId === "__all__";
    if (isSuper && isAllCompaniesMode) {
        return (
            <div className="space-y-5" data-testid="pec-inbox-page">
                <PageHeader
                    title="PEC in arrivo"
                    subtitle="Priorità sulle PEC degli ultimi 14 giorni."
                    breadcrumbs={[{ label: "PEC" }, { label: "In arrivo" }]}
                />
                <div
                    data-testid="pec-inbox-select-company-banner"
                    className="rounded-xl border border-[#F59E0B]/40 bg-[#FFFBEB] text-[#92400E] px-5 py-4 text-sm"
                >
                    Seleziona un&apos;azienda dal menu in alto per visualizzare le PEC in arrivo.
                    In modalità &quot;Tutte le aziende&quot; l&apos;inbox non è disponibile.
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="pec-inbox-page">
            <PageHeader
                title="PEC in arrivo"
                subtitle="Priorità sulle PEC degli ultimi 14 giorni. Aggiornamento automatico ogni 30 secondi."
                breadcrumbs={[{ label: "PEC" }, { label: "In arrivo" }]}
            />
            <CompanyPecBanner />

            {/* Counters */}
            <div className="flex flex-wrap gap-3">
                <CounterCard testid="pec-inbox-counter-today" icon={Calendar} label="Oggi" value={counters.today} />
                <CounterCard testid="pec-inbox-counter-14days" icon={Clock} label="Ultimi 14 giorni" value={counters.last_14_days} />
                <CounterCard testid="pec-inbox-counter-in-pipeline" icon={Activity} label="In coda analisi" value={counters.in_pipeline} pulse />
            </div>

            {/* Filters bar */}
            <div className="bg-white border border-[#DCE3EE] rounded-xl p-3 flex flex-wrap items-center gap-2">
                <select
                    data-testid="pec-inbox-filter-source"
                    value={filters.source}
                    onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                    className="text-xs border border-[#DCE3EE] rounded-md px-2 py-2 bg-white text-[#172033]"
                >
                    <option value="all">Tutte le sorgenti</option>
                    <option value="manual">📥 Upload manuali</option>
                    {mailboxes.map((m) => (
                        <option key={m.id} value={m.id}>📮 {m.display_name || m.pec_address}</option>
                    ))}
                </select>
                <select
                    data-testid="pec-inbox-filter-pipeline-status"
                    value={filters.pipeline_status}
                    onChange={(e) => setFilters({ ...filters, pipeline_status: e.target.value })}
                    className="text-xs border border-[#DCE3EE] rounded-md px-2 py-2 bg-white text-[#172033]"
                >
                    <option value="all">Tutti gli stati pipeline</option>
                    <option value="completed">Completate</option>
                    <option value="running">In corso</option>
                    <option value="error">Con errori</option>
                </select>
                <input
                    type="date"
                    data-testid="pec-inbox-filter-from"
                    value={filters.from}
                    onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                    className="text-xs border border-[#DCE3EE] rounded-md px-2 py-2 bg-white text-[#172033]"
                />
                <span className="text-xs text-[#64748B]">→</span>
                <input
                    type="date"
                    data-testid="pec-inbox-filter-to"
                    value={filters.to}
                    onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                    className="text-xs border border-[#DCE3EE] rounded-md px-2 py-2 bg-white text-[#172033]"
                />
                <label className="flex items-center gap-1.5 text-xs text-[#172033] cursor-pointer ml-1">
                    <input
                        type="checkbox"
                        data-testid="pec-inbox-filter-include-older"
                        checked={filters.include_older}
                        onChange={(e) => setFilters({ ...filters, include_older: e.target.checked })}
                    />
                    Mostra più vecchie di 14gg
                </label>
                <div className="flex-1" />
                {autoPaused && (
                    <span className="text-[11px] text-[#F59E0B] font-medium">
                        ⚠ Aggiornamento automatico in pausa
                    </span>
                )}
                <button
                    data-testid="pec-inbox-refresh-btn"
                    onClick={() => load(false)}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-[#DCE3EE] hover:bg-[#F5F7FB] text-[#172033]"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
                </button>
            </div>

            {/* Table or empty */}
            {items.length === 0 ? (
                filtersActive ? (
                    <div data-testid="pec-inbox-empty-state">
                        <EmptyState
                            icon={Search}
                            title="Nessun risultato con i filtri correnti"
                            description="Prova a rimuovere alcuni filtri o ad ampliare l'intervallo di date."
                            action={<Button onClick={clearFilters} variant="outline">Rimuovi filtri</Button>}
                        />
                    </div>
                ) : (
                    <div data-testid="pec-inbox-empty-state">
                        <EmptyState
                            icon={Inbox}
                            title="Nessuna PEC ricevuta negli ultimi 14 giorni"
                            description="Le PEC che arrivano tramite le tue caselle IMAP configurate o quelle caricate manualmente appariranno qui in tempo reale."
                            action={
                                <div className="flex gap-2">
                                    <Button onClick={() => navigate("/pec/upload")}
                                        data-testid="pec-inbox-empty-upload-btn"
                                        className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
                                        <Upload className="w-4 h-4 mr-1.5" /> Carica manualmente
                                    </Button>
                                    <Button onClick={() => navigate("/integrations")}
                                        data-testid="pec-inbox-empty-imap-btn"
                                        variant="outline">
                                        <Mail className="w-4 h-4 mr-1.5" /> Configura casella IMAP
                                    </Button>
                                </div>
                            }
                        />
                    </div>
                )
            ) : (
                <div className="bg-white border border-[#DCE3EE] rounded-xl overflow-x-auto">
                    <table className="w-full text-sm" data-testid="pec-inbox-table">
                        <thead className="bg-[#F5F7FB] text-[11px] uppercase text-[#64748B]">
                            <tr>
                                <th className="text-left px-3 py-2.5 w-[62px]">Data</th>
                                <th className="text-left px-3 py-2.5 w-[176px]">Mittente</th>
                                <th className="text-left px-3 py-2.5 w-[140px]">Destinatari</th>
                                <th className="text-left px-3 py-2.5">Oggetto</th>
                                <th className="text-left px-3 py-2.5 w-[100px]">Pipeline</th>
                                <th className="text-left px-3 py-2.5 w-[118px]">Stato tecnico</th>
                                <th className="text-left px-3 py-2.5 w-[84px]">Rischio</th>
                                <th className="text-right px-3 py-2.5 w-[52px]">Azioni</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#DCE3EE]">
                            {items.map((p) => (
                                <tr key={p.id}
                                    data-testid={`pec-inbox-row-${p.id}`}
                                    data-pipeline-status={p.pipeline_status}
                                    className="group hover:bg-[#F8FAFC] transition-colors">
                                    <td className="px-3 py-3 align-top">
                                        <DateCell iso={p.received_at} />
                                    </td>
                                    <td className="px-3 py-3 text-sm align-top">
                                        {p.is_pec_receipt && p.recipient_original ? (
                                            <div className="max-w-[160px]">
                                                <div className="text-[#172033] font-medium flex items-center gap-1.5">
                                                    <span className="text-[9px] font-mono font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#475569] border border-[#DCE3EE] shrink-0" data-testid={`pec-receipt-badge-${p.id}`}>ricevuta</span>
                                                    <span className="truncate" title={p.recipient_original}>{p.recipient_original}</span>
                                                </div>
                                                <div className="text-[11px] text-[#94A3B8] truncate" title={p.from_address}>via {p.from_address}</div>
                                                <div className="text-[10px] text-[#94A3B8] truncate mt-0.5" title={p.source_label}>
                                                    {p.source_type === "mailbox" ? "📮" : "📥"}&nbsp;{p.source_label}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="max-w-[160px]">
                                                <div className="text-[#172033] font-medium truncate" title={p.from_name || ""}>{p.from_name || "—"}</div>
                                                <div className="text-[11px] text-[#64748B] truncate" title={p.from_address || ""}>{p.from_address}</div>
                                                <div className="text-[10px] text-[#94A3B8] truncate mt-0.5" title={p.source_label}>
                                                    {p.source_type === "mailbox" ? "📮" : "📥"}&nbsp;{p.source_label}
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-3 text-xs align-top" data-testid={`pec-inbox-recipients-${p.id}`}>
                                        <RecipientsCell recipients={p.recipients || []} count={p.recipients_count || 0} />
                                    </td>
                                    <td className="px-3 py-3 text-sm text-[#172033] align-top" title={p.subject || ""}>
                                        <div className="truncate">{p.subject || "—"}</div>
                                    </td>
                                    <td className="px-3 py-3 align-top">
                                        <PipelineBadge status={p.pipeline_status}
                                            step={p.pipeline_current_step} name={p.pipeline_step_name} />
                                    </td>
                                    <td className="px-3 py-3 align-top">
                                        <TechnicalStatusBadge status={p.technical_status} />
                                    </td>
                                    <td className="px-3 py-3 align-top">
                                        {p.pipeline_status === "completed" && typeof p.operational_risk === "number"
                                            ? <RiskBadge level={riskLevel(p.operational_risk)} />
                                            : <span className="text-xs text-[#64748B]">—</span>}
                                    </td>
                                    <td className="px-3 py-3 text-right align-top transition-colors group-hover:bg-[#EFF6FF] rounded-r-md">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button className="p-1.5 rounded hover:bg-[#F5F7FB] text-[#64748B]"
                                                    data-testid={`pec-inbox-action-menu-${p.id}`}>
                                                    <MoreHorizontal className="w-4 h-4" />
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="min-w-[180px]">
                                                <DropdownMenuItem
                                                    data-testid={`pec-inbox-action-open-${p.id}`}
                                                    onClick={() => navigate(`/pec/analyses/${p.id}`)}
                                                >
                                                    <ExternalLink className="w-3.5 h-3.5 mr-2" /> Apri
                                                </DropdownMenuItem>
                                                {canReanalyze && (
                                                    <DropdownMenuItem
                                                        data-testid={`pec-inbox-action-reanalyze-${p.id}`}
                                                        disabled={p.pipeline_status === "running" || busy === p.id}
                                                        onClick={() => setReanalyzeFor(p)}
                                                        title={p.pipeline_status === "running" ? "Analisi già in corso" : ""}
                                                    >
                                                        <RotateCw className="w-3.5 h-3.5 mr-2" /> Ri-analizza
                                                    </DropdownMenuItem>
                                                )}
                                                {canDelete && (
                                                    <>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem
                                                            data-testid={`pec-inbox-action-delete-${p.id}`}
                                                            onClick={() => { setDeleteFor(p); setDeleteReason(""); }}
                                                            className="text-[#DC2626] focus:text-[#DC2626]"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Elimina
                                                        </DropdownMenuItem>
                                                    </>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Re-analyze confirm */}
            <Dialog open={!!reanalyzeFor} onOpenChange={(v) => !v && setReanalyzeFor(null)}>
                <DialogContent data-testid="pec-reanalyze-modal">
                    <DialogHeader>
                        <DialogTitle>Ri-analizzare questa PEC?</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-[#64748B]">
                        La pipeline verrà rilanciata sul file .eml originale. I risultati precedenti
                        (score, fattori di rischio, analisi AI) verranno sostituiti.
                    </div>
                    {reanalyzeFor && (
                        <div className="text-xs text-[#172033] bg-[#F5F7FB] p-3 rounded-md">
                            <div><strong>Mittente:</strong> {reanalyzeFor.from_address}</div>
                            <div><strong>Oggetto:</strong> {reanalyzeFor.subject}</div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReanalyzeFor(null)}>Annulla</Button>
                        <Button onClick={doReanalyze}
                            data-testid="pec-reanalyze-confirm-btn"
                            disabled={busy === reanalyzeFor?.id}
                            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
                            {busy === reanalyzeFor?.id ? "Avvio…" : "Ri-analizza"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteFor} onOpenChange={(v) => !v && setDeleteFor(null)}>
                <DialogContent data-testid="pec-delete-modal">
                    <DialogHeader>
                        <DialogTitle>Eliminare questa PEC?</DialogTitle>
                    </DialogHeader>
                    <div className="text-sm text-[#64748B]">
                        L&apos;eliminazione è soft-delete: la PEC verrà nascosta ma il file .eml resta in archivio
                        secondo la retention policy aziendale. Motivo obbligatorio (min 10 caratteri).
                    </div>
                    <Textarea
                        data-testid="pec-delete-reason-input"
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        placeholder="Motivo dell'eliminazione (obbligatorio)"
                        rows={3}
                        className="mt-1"
                    />
                    <div className="text-[10px] text-[#64748B]">{deleteReason.length}/10 caratteri minimi</div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteFor(null)}>Annulla</Button>
                        <Button
                            data-testid="pec-delete-confirm-btn"
                            onClick={doDelete}
                            disabled={deleteReason.trim().length < 10 || busy === deleteFor?.id}
                            className="bg-[#DC2626] hover:bg-[#B91C1C] text-white">
                            {busy === deleteFor?.id ? "Eliminazione…" : "Elimina PEC"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
